// frontend/src/pages/OperatorDashboard.tsx
// Версия: 3.5.2 (2026-01-29)
// ИЗМЕНЕНИЯ v3.5.2:
// - Устранён дубль данных между UnmatchedSignalsPanel и SignalsUnmatchedTable:
//   панель — единственный источник, таблица получает externalItems (без повторного API fetch)

import { apiUrl } from '../config/paths';
import { getShiftPeriod } from '../utils/shiftPeriod';
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import AssessmentIcon from '@mui/icons-material/Assessment';

import UnmatchedSignalsPanel from '../components/operator/UnmatchedSignalsPanel';
import { getRequestStatusLabel, getRequestStatusColor, isRealAlert } from '../utils/statusLabels';
import AutoConfirmToggle from '../components/AutoConfirmToggle';
import ModeIndicator from '../components/ModeIndicator';
import { useAuth } from '../contexts/AuthContext';
import OperatorSelectionDialog from '../components/OperatorSelectionDialog';
import { OPERATORS_VERSION } from '../constants/operators.const';

interface Request {
  id: number;
  vessel_name?: string;
  vesselName?: string;
  mmsi: string;
  imo_number?: string;
  imoNumber?: string;
  ssas_number?: string;
  terminal_number?: string;
  terminalNumber?: string;
  test_date?: string;
  testDate?: string;
  planned_test_date?: string;
  status: string;
  email?: string;
  contact_email?: string;
  clientEmail?: string;
  created_at?: string;
  updated_at?: string;
  contact_person?: string;
  contactPerson?: string;
  phone?: string;
  company_name?: string;
  companyName?: string;
  port_of_registry?: string;
  portOfRegistry?: string;
  notes?: string;
  start_time?: string;
  end_time?: string;
  source?: string;
  rejection_reason?: string;
}

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
};

const EXTERNAL_RESOURCES = {
  ITU_MARS: {
    name: 'ITU MARS Ship Station List',
    url: 'https://www.itu.int/mmsapp/shipstation/list',
    description: 'База данных ITU MARS',
    icon: '🌐',
  },
  INMARSAT: {
    name: 'Inmarsat Ships Directory',
    url: 'https://www.inmarsat.com/customer-service/ships-directory/',
    description: 'Официальный справочник судов Inmarsat',
    icon: '🛰️',
  },
};

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

function normalizeVesselName(req: Request): string {
  return (req.vessel_name || req.vesselName || '').trim() || '—';
}
function normalizeTerminal(req: Request): string {
  return (req.terminal_number || req.terminalNumber || req.ssas_number || '').trim() || '—';
}
function normalizeImo(req: Request): string {
  return (req.imo_number || req.imoNumber || '').trim() || '—';
}
function normalizeEmail(req: Request): string {
  return (req.contact_email || req.clientEmail || req.email || '').trim() || '—';
}
function normalizeTestDate(req: Request): string {
  const raw = req.test_date || req.testDate || req.planned_test_date;
  if (!raw) return '—';
  try {
    const str = String(raw).trim();
    const d = new Date(str);
    if (isNaN(d.getTime())) return str;

    // Date-only (2026-03-12T00:00:00.000Z или 2026-03-12) → только дата по UTC
    // Preserve date-only values without inventing a display time.
    const isDateOnly = str.includes('T00:00:00') || str.length === 10;
    if (isDateOnly) {
      return `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
    }

    // С конкретным временем → дата + время UTC
    return (
      d.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  } catch {
    return String(raw);
  }
}
function normalizeCompanyName(req: Request): string {
  return (req.company_name || req.companyName || '').trim() || '—';
}
function normalizePortOfRegistry(req: Request): string {
  return (req.port_of_registry || req.portOfRegistry || '').trim() || '—';
}
function normalizePhone(req: Request): string {
  return (req.phone || '').trim() || '—';
}
function normalizeNotes(req: Request): string {
  return (req.notes || '').trim() || '—';
}

function formatDateTime(dateStr?: string): string {
  if (!dateStr) return '—';
  try {
    return (
      new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }) + ' UTC'
    );
  } catch {
    return dateStr;
  }
}

export default function OperatorDashboard() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = user?.role === 'admin';
  const _nav = useNavigate();

  // OperatorSelectionDialog state
  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);

  const shift = useMemo(() => getShiftPeriod(), []);

  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailsPendingReview, setEmailsPendingReview] = useState(0);

  // Auto-confirm mode
  const [autoConfirmEnabled, setAutoConfirmEnabled] = useState<boolean>(false);
  const [autoConfirmLoading, setAutoConfirmLoading] = useState<boolean>(false);
  const [autoConfirmUpdating, setAutoConfirmUpdating] = useState<boolean>(false);

  // details dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);

  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'info',
  });

  const mountedRef = useRef(true);
  const requestsAbortRef = useRef<AbortController | null>(null);
  const settingsAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      requestsAbortRef.current?.abort();
      settingsAbortRef.current?.abort();
    };
  }, []);

  const loadAutoConfirm = useCallback(async () => {
    settingsAbortRef.current?.abort();
    const controller = new AbortController();
    settingsAbortRef.current = controller;

    if (mountedRef.current) setAutoConfirmLoading(true);

    try {
      const res = await fetch(apiUrl('/admin/settings/ssto'), {
        method: 'GET',
        headers: getAuthHeaders(),
        signal: controller.signal,
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || `Ошибка загрузки настроек: ${res.status}`);
      }

      const enabled =
        typeof data?.enabled === 'boolean'
          ? data.enabled
          : typeof data?.auto_confirm_enabled === 'boolean'
            ? data.auto_confirm_enabled
            : typeof data?.autoConfirmEnabled === 'boolean'
              ? data.autoConfirmEnabled
              : false;

      if (mountedRef.current) setAutoConfirmEnabled(Boolean(enabled));
    } catch (e: unknown) {
      if ((e as any)?.name === 'AbortError') return;
      console.error(e);
    } finally {
      if (mountedRef.current) setAutoConfirmLoading(false);
    }
  }, []);

  const handleAutoConfirmToggle = useCallback(async (enabled: boolean) => {
    if (mountedRef.current) setAutoConfirmUpdating(true);
    try {
      const res = await fetch(apiUrl('/admin/settings/ssto/auto-confirm'), {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.message || `Ошибка сохранения: ${res.status}`);
      }

      if (mountedRef.current) {
        setAutoConfirmEnabled(Boolean(enabled));
        setSnackbar({
          open: true,
          message: enabled ? 'Автоподтверждение включено' : 'Автоподтверждение выключено',
          severity: 'success',
        });
      }
    } catch (e: unknown) {
      if (mountedRef.current) {
        setSnackbar({
          open: true,
          message: e instanceof Error ? e.message : 'Ошибка сохранения режима',
          severity: 'error',
        });
      }
    } finally {
      if (mountedRef.current) setAutoConfirmUpdating(false);
    }
  }, []);

  const loadRequests = useCallback(async () => {
    requestsAbortRef.current?.abort();
    const controller = new AbortController();
    requestsAbortRef.current = controller;

    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }

    try {
      // Загружаем ВСЕ заявки за период (без серверной фильтрации по статусу — чтобы включить approved/matched)
      const params = new URLSearchParams();
      params.append('limit', '200');
      params.append('sortBy', 'created_at');
      params.append('sortOrder', 'DESC');

      const res = await fetch(apiUrl(`/requests?${params.toString()}`), {
        method: 'GET',
        headers: getAuthHeaders(),
        signal: controller.signal,
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || `Ошибка загрузки заявок: ${res.status}`);

      let list: Request[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.items)
            ? data.items
            : [];

      // Клиентская фильтрация по дате теста: ±1 день от сегодня
      const now = new Date();
      const dayMs = 24 * 60 * 60 * 1000;
      const dateFrom = new Date(now.getTime() - dayMs);
      const dateTo = new Date(now.getTime() + dayMs);
      list = list.filter((r: Request) => {
        const rawDate = r.test_date || r.testDate || r.planned_test_date;
        const testDate = rawDate ? new Date(rawDate) : null;
        if (!testDate) return false; // без даты теста — не показываем
        return testDate >= dateFrom && testDate <= dateTo;
      });

      if (mountedRef.current) setRequests(list);
    } catch (e: unknown) {
      if ((e as any)?.name === 'AbortError') return;
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Ошибка загрузки заявок');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
    loadAutoConfirm();
  }, [loadRequests, loadAutoConfirm]);

  // FUNC-019: Загрузка количества писем на ручном разборе
  useEffect(() => {
    const loadEmailPending = async () => {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
        const res = await fetch(apiUrl('email-logs/pending?limit=1&offset=0'), {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        setEmailsPendingReview(data.total || 0);
      } catch (e) {
        console.warn('FUNC-019: не удалось загрузить статус почты', e);
      }
    };
    loadEmailPending();
  }, []);

  // Show operator selection dialog for operators without saved info or with outdated version
  useEffect(() => {
    if (!authLoading && user?.role === 'operator') {
      const saved = localStorage.getItem('operator_info');
      if (!saved) {
        setOperatorDialogOpen(true);
        return;
      }
      try {
        const parsed = JSON.parse(saved);
        if (parsed._v !== OPERATORS_VERSION) {
          // Версия списка операторов изменилась — сбросить и перевыбрать
          localStorage.removeItem('operator_info');
          setOperatorDialogOpen(true);
        }
      } catch {
        localStorage.removeItem('operator_info');
        setOperatorDialogOpen(true);
      }
    }
  }, [authLoading, user]);

  const handleRowClick = (request: Request) => {
    setSelectedRequest(request);
    setDetailsDialogOpen(true);
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedRequest(null);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" fontWeight="bold">
            Панель оперативного дежурного ГМСКЦ{isAdmin ? ' (администратор)' : ''}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Период дежурства: {shift.label}
          </Typography>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          {isAdmin ? (
            <AutoConfirmToggle
              enabled={autoConfirmEnabled}
              onToggle={handleAutoConfirmToggle}
              loading={autoConfirmLoading}
              updating={autoConfirmUpdating}
              compact={true}
            />
          ) : (
            <ModeIndicator
              mode={autoConfirmEnabled ? 'auto' : 'manual'}
              loading={autoConfirmLoading}
            />
          )}

          <Button
            variant="outlined"
            size="small"
            startIcon={<AssessmentIcon />}
            onClick={() => {
              const now = new Date();
              const hour = now.getUTCHours();
              const dutyStart = new Date(now);
              if (hour >= 8) {
                dutyStart.setUTCHours(8, 0, 0, 0);
              } else {
                dutyStart.setUTCDate(dutyStart.getUTCDate() - 1);
                dutyStart.setUTCHours(8, 0, 0, 0);
              }
              const dutyEnd = new Date(dutyStart);
              dutyEnd.setUTCDate(dutyEnd.getUTCDate() + 1);
              const prefix = user?.role === 'admin' ? '/admin' : '/operator';
              _nav(
                `${prefix}/duty-report?from=${dutyStart.toISOString()}&to=${dutyEnd.toISOString()}`
              );
            }}
          >
            Отчёт за смену
          </Button>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadRequests}
            disabled={loading}
          >
            Обновить
          </Button>
        </Stack>
      </Box>

      {/* ========== ВНЕШНИЕ РЕСУРСЫ ========== */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }} elevation={1}>
        <Typography variant="subtitle1" gutterBottom fontWeight="medium" color="text.secondary">
          Внешние ресурсы для поиска информации о судах
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            component="a"
            href={EXTERNAL_RESOURCES.ITU_MARS.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {EXTERNAL_RESOURCES.ITU_MARS.icon} {EXTERNAL_RESOURCES.ITU_MARS.name}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            component="a"
            href={EXTERNAL_RESOURCES.INMARSAT.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {EXTERNAL_RESOURCES.INMARSAT.icon} {EXTERNAL_RESOURCES.INMARSAT.name}
          </Button>
        </Stack>
      </Paper>

      {/* Ошибки загрузки */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ========== ПАНЕЛЬ НЕСОПОСТАВЛЕННЫХ СИГНАЛОВ ========== */}
      <Paper sx={{ p: 2, mb: 3 }} elevation={2}>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Несопоставленные сигналы
        </Typography>

        <UnmatchedSignalsPanel
          onSignalLinked={(signalId, requestId) => {
            setSnackbar({
              open: true,
              message: `Сигнал #${signalId} связан с заявкой #${requestId}`,
              severity: 'success',
            });
            loadRequests();
          }}
          onSignalDismissed={(signalId, reason) => {
            setSnackbar({
              open: true,
              message: `Сигнал #${signalId} отклонён: ${reason}`,
              severity: 'info',
            });
          }}
        />
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* ========== ТАБЛИЦА ЗАЯВОК ========== */}
      <Paper sx={{ p: 2 }} elevation={2}>
        <Typography variant="h6" gutterBottom fontWeight="medium">
          Заявки на тестирование
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          💡 Кликните на строку для просмотра деталей заявки
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Судно</TableCell>
                  <TableCell>MMSI</TableCell>
                  <TableCell>IMO</TableCell>
                  <TableCell>Терминал</TableCell>
                  <TableCell>Дата теста</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Статус</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {requests.map((r) => {
                  const danger = isRealAlert(r.status);

                  return (
                    <TableRow
                      key={r.id}
                      hover
                      onClick={() => handleRowClick(r)}
                      sx={{
                        cursor: 'pointer',
                        ...(danger ? { backgroundColor: '#ffebee' } : {}),
                        '&:hover': { backgroundColor: danger ? '#ffcdd2' : '#e3f2fd' },
                      }}
                    >
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{normalizeVesselName(r)}</TableCell>
                      <TableCell>{r.mmsi || '—'}</TableCell>
                      <TableCell>{normalizeImo(r)}</TableCell>
                      <TableCell>{normalizeTerminal(r)}</TableCell>
                      <TableCell>{normalizeTestDate(r)}</TableCell>
                      <TableCell>{normalizeEmail(r)}</TableCell>
                      <TableCell>
                        <Chip
                          label={getRequestStatusLabel(r.status || '')}
                          color={getRequestStatusColor(r.status || '')}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      Нет данных по текущим фильтрам
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* ========= ДИАЛОГ ДЕТАЛЕЙ ========= */}
      <Dialog open={detailsDialogOpen} onClose={handleCloseDetailsDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#1976d2',
            color: 'white',
          }}
        >
          <Typography variant="h6">
            📋 Детальная информация о заявке #{selectedRequest?.id}
          </Typography>
          <IconButton onClick={handleCloseDetailsDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  🚢 Информация о судне
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Название судна:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {normalizeVesselName(selectedRequest)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  MMSI:
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {selectedRequest.mmsi || '—'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  IMO номер:
                </Typography>
                <Typography variant="body1">{normalizeImo(selectedRequest)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Терминал ССТО:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: '#e3f2fd',
                    px: 1,
                    borderRadius: 1,
                    display: 'inline-block',
                  }}
                >
                  {normalizeTerminal(selectedRequest)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Порт приписки:
                </Typography>
                <Typography variant="body1">{normalizePortOfRegistry(selectedRequest)}</Typography>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  🧪 Данные тестирования
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дата создания заявки
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Интервал теста (UTC)
                </Typography>
                <Typography variant="body1">
                  {selectedRequest.start_time || selectedRequest.end_time ? (
                    <>
                      Начало:{' '}
                      {selectedRequest.start_time
                        ? new Date(selectedRequest.start_time).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                          }) + ' UTC'
                        : '—'}
                      <br />
                      Окончание:{' '}
                      {selectedRequest.end_time
                        ? new Date(selectedRequest.end_time).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'UTC',
                          }) + ' UTC'
                        : '—'}
                    </>
                  ) : (
                    normalizeTestDate(selectedRequest)
                  )}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Статус:
                </Typography>
                <Chip
                  label={getRequestStatusLabel(selectedRequest.status)}
                  color={getRequestStatusColor(selectedRequest.status)}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Последнее обновление
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.updated_at)}
                </Typography>
              </Grid>
              {selectedRequest.source && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Источник:
                  </Typography>
                  <Typography variant="body1">
                    {selectedRequest.source === 'WEB_FORM'
                      ? 'Веб-форма'
                      : selectedRequest.source === 'MORSPAS'
                        ? 'Морспас'
                        : selectedRequest.source === 'MORFLOT'
                          ? 'Морфлот'
                          : selectedRequest.source}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  📞 Контактные данные судовладельца
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Организация:
                </Typography>
                <Typography variant="body1">{normalizeCompanyName(selectedRequest)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Телефон:
                </Typography>
                <Typography variant="body1">{normalizePhone(selectedRequest)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Email:
                </Typography>
                <Typography variant="body1">{normalizeEmail(selectedRequest)}</Typography>
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="primary" gutterBottom>
                  ⚙️ Системная информация
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Дата создания заявки:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Последнее обновление:
                </Typography>
                <Typography variant="body1">
                  {formatDateTime(selectedRequest.updated_at)}
                </Typography>
              </Grid>

              {normalizeNotes(selectedRequest) !== '—' && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    Примечания:
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ bgcolor: '#f5f5f5', p: 1, borderRadius: 1, mt: 0.5 }}
                  >
                    {normalizeNotes(selectedRequest)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetailsDialog} variant="contained">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========== SNACKBAR ========== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ========== OPERATOR SELECTION DIALOG ========== */}
      <OperatorSelectionDialog
        open={operatorDialogOpen}
        onClose={(operator) => {
          if (operator) {
            setOperatorDialogOpen(false);
          }
        }}
      />
    </Box>
  );
}
